// IE Hub - Engine Effects Registry
// Provides vetted, sandboxed effects that can be invoked by JSON actions

// Type definitions
export interface NavigateDeps {
  navigate?: (to: string, options?: { replace?: boolean }) => void;
}

export interface FormApiDeps {
  formApi?: {
    setValue?: (name: string, value: unknown, options?: { shouldDirty?: boolean; shouldValidate?: boolean }) => void;
    reset?: (values?: Record<string, unknown>, options?: Record<string, unknown>) => void;
    trigger?: (name?: string | string[]) => Promise<boolean>;
  };
}

export interface EffectDeps extends NavigateDeps, FormApiDeps {}

export interface AlertParams {
  severity?: 'error' | 'warning' | 'info' | 'success';
  message?: string;
  title?: string;
  variant?: 'filled' | 'outlined' | 'standard';
  autoHide?: boolean;
  duration?: number;
}

export interface NavigateParams {
  to?: string;
  url?: string;
  replace?: boolean;
}

export interface SetFieldValueParams {
  name?: string;
  value?: unknown;
}

export interface ResetFormParams {
  values?: Record<string, unknown>;
  options?: Record<string, unknown>;
}

export interface TriggerParams {
  name?: string | string[];
}

export interface ActionBase {
  type: string;
}

export interface AlertAction extends ActionBase, AlertParams {
  type: 'alert' | 'raiseAlert';
}

export interface NavigateAction extends ActionBase, NavigateParams {
  type: 'navigate';
}

export interface SetFieldValueAction extends ActionBase, SetFieldValueParams {
  type: 'setFieldValue';
}

export interface ResetFormAction extends ActionBase, ResetFormParams {
  type: 'resetForm';
}

export interface TriggerAction extends ActionBase, TriggerParams {
  type: 'trigger';
}

export type Effect = AlertAction | NavigateAction | SetFieldValueAction | ResetFormAction | TriggerAction;

export interface EffectRegistry {
  raiseAlert: (params?: AlertParams) => Promise<void>;
  navigate: (params?: NavigateParams) => Promise<void>;
  setFieldValue: (params?: SetFieldValueParams) => Promise<void>;
  resetForm: (params?: ResetFormParams) => Promise<void>;
  trigger: (params?: TriggerParams) => Promise<void>;
}

// Utilities
const isRelativeUrl = (url: unknown): url is string => 
  typeof url === 'string' && url.startsWith('/') && !url.startsWith('//');

/**
 * Create effects with injected dependencies
 * @param deps - Dependencies required by some effects
 */
export function createEffects(deps: EffectDeps = {}): EffectRegistry {
  const { navigate, formApi } = deps;

  return {
    // Show an alert via global event; AlertProvider listens to this event
    raiseAlert: async (params: AlertParams = {}) => {
      const detail = {
        severity: params.severity || 'info',
        message: params.message || '',
        title: params.title,
        variant: params.variant || 'filled',
        autoHide: params.autoHide,
        duration: params.duration,
      };
      window.dispatchEvent(new CustomEvent('iehub-alert', { detail }));
    },

    // Navigate using React Router; only allow relative URLs
    navigate: async (params: NavigateParams = {}) => {
      const to = params.to || params.url;
      if (!to || !isRelativeUrl(to)) {
        throw new Error('Navigation target must be a relative URL starting with "/"');
      }
      if (typeof navigate === 'function') {
        navigate(to, { replace: !!params.replace });
      } else {
        // Fallback to location change if navigate is unavailable
        window.location.assign(to);
      }
    },

    // Set a form field value if form API is provided
    // Use shouldValidate: false to prevent cascading validations that can cause infinite loops
    // and reset checkbox/radio states during event handling
    setFieldValue: async (params: SetFieldValueParams = {}) => {
      if (!formApi || typeof formApi.setValue !== 'function') return;
      if (!params.name) return;
      formApi.setValue(params.name, params.value, { shouldDirty: true, shouldValidate: false });
    },

    // Reset the form if form API is provided
    resetForm: async (params: ResetFormParams = {}) => {
      if (!formApi || typeof formApi.reset !== 'function') return;
      formApi.reset(params.values || undefined, params.options || undefined);
    },

    // Trigger form validation/re-render for specified field(s) or all fields
    trigger: async (params: TriggerParams = {}) => {
      if (!formApi || typeof formApi.trigger !== 'function') return;
      await formApi.trigger(params.name);
    },
  };
}

/**
 * Execute an array of actions using the provided effects
 * @param actions - Action objects from schema
 * @param deps - Dependencies for effects
 * @param context - Optional context passed to effects (not used by defaults)
 */
export async function runEffects(
  actions: unknown[], 
  deps: EffectDeps = {}, 
  _context: Record<string, unknown> = {}
): Promise<void> {
  if (!Array.isArray(actions)) return;
  const effects = createEffects(deps);

  for (const action of actions) {
    try {
      if (!action || typeof action !== 'object') continue;
      const actionObj = action as ActionBase;
      const type = actionObj.type;
      
      if (type === 'alert' || type === 'raiseAlert') {
        await effects.raiseAlert(action as AlertParams);
      } else if (type === 'navigate') {
        await effects.navigate(action as NavigateParams);
      } else if (type === 'setFieldValue') {
        await effects.setFieldValue(action as SetFieldValueParams);
      } else if (type === 'resetForm') {
        await effects.resetForm(action as ResetFormParams);
      } else if (type === 'trigger') {
        await effects.trigger(action as TriggerParams);
      } else {
        // Unknown actions are ignored but logged in dev
        if (import.meta.env.DEV) {
          console.warn('Unknown effect type:', type);
        }
      }
    } catch (err) {
      console.error('Error executing effect', (action as ActionBase)?.type, err);
    }
  }
}

export default {
  createEffects,
  runEffects,
};
